import {
    HStack,
    VStack,
    Text,
    Tooltip,
    Box,
    Icon,
    FormControl,
    Input,
    IconButton,
    Button,
    BoxProps,
    StackProps,
    chakra,
    Divider,
    FormLabel,
    StackDivider,
    Wrap,
    Badge,
    Switch,
} from '@chakra-ui/react';
import React from 'react';
import { ReportingViewBaseConfig } from '../../../../base';
import { FormBuilderViewProps } from './formBuilderProps';
import { SharePopoverContainerProps, SharePopoverViewProps } from './share';
import TextareaAutosize from 'react-textarea-autosize';
import { FieldListContainerProps } from '../../../common';
import { FormElementOption, FormElementViewProps } from './element';
import { assertNever } from '@varos/util-typescript';
import { CreatableSelect, GroupBase, SelectComponentsConfig } from 'chakra-react-select';
import { HiDotsHorizontal } from 'react-icons/hi';

const ChakraTextareaAutosize = chakra(TextareaAutosize, {
    baseStyle: {
        // any default Chakra styles you want here
        // e.g. fontSize: "md", etc.
    },
});

export function createFormBuilderView(
    config: ReportingViewBaseConfig & {
        Container: {
            Share: {
                Popover: React.FC<SharePopoverContainerProps>;
            };
            Field: {
                List: React.FC<FieldListContainerProps>;
            };
        };
    }
): React.FC<FormBuilderViewProps> {
    const {
        UI: { View },
        Container: {
            Field: { List: FieldListContainer },
            Share: { Popover: ShareContainer },
        },
    } = config;

    const ShareMenuBase = View.createMenu({});
    const MenuBase = View.createMenu({});

    const ShareMenu: React.FC<SharePopoverViewProps> = (props) => {
        return (
            <ShareMenuBase {...props.menu} minW="24rem">
                {props.children}
            </ShareMenuBase>
        );
    };

    const SectionList: React.FC<React.PropsWithChildren<{}>> = (props) => {
        return (
            <VStack
                w="full"
                align="start"
                divider={
                    <Box
                        w="full"
                        borderBottomWidth={`3 !important`}
                        borderColor="whiteAlpha.200"
                        borderStyle="dashed"
                    />
                }
                spacing={{ base: 4, md: 8, xl: 12, '2xl': 16 }}
            >
                {props.children}
            </VStack>
        );
    };

    const SectionItem: React.FC<
        React.PropsWithChildren<{
            title: React.ReactNode;
            description: React.ReactNode;
            variant: 'bordered' | 'unstyled';
            secondaryContent?: React.ReactNode;
        }>
    > = (props) => {
        return (
            <VStack spacing={{ base: 2, md: 4, xl: 6, '2xl': 8 }} w="full" align="start">
                <VStack
                    spacing={{ base: 1, md: 2, xl: 3, '2xl': 4 }}
                    w="full"
                    align="start"
                >
                    <HStack w="full" justify="space-between">
                        <Text color="whiteAlpha.900" fontWeight="semibold" fontSize="xl">
                            {props.title}
                        </Text>
                        {props.secondaryContent}
                    </HStack>
                    <HStack>
                        <Text color="whiteAlpha.700" maxW="32rem">
                            {props.description}
                        </Text>
                    </HStack>
                </VStack>
                <VStack
                    w="full"
                    spacing={{ base: 2, md: 4, xl: 6, '2xl': 8 }}
                    {...(props.variant === 'bordered'
                        ? {
                              borderWidth: 1,
                              borderColor: 'whiteAlpha.300',
                              borderRadius: 'lg',
                          }
                        : {})}
                >
                    <VStack
                        spacing={0}
                        w="full"
                        align="start"
                        divider={<StackDivider borderColor="whiteAlpha.300" />}
                    >
                        {props.children}
                    </VStack>
                </VStack>
            </VStack>
        );
    };

    const InputControl: React.FC<
        React.PropsWithChildren<{
            label: React.ReactNode;
            description: React.ReactNode | null;
        }>
    > = (props) => {
        return (
            <FormControl
                w="full"
                as={VStack}
                align="start"
                spacing={{ base: 2, md: 2, xl: 4, '2xl': 4 }}
                p={{ base: 2, md: 4, xl: 6, '2xl': 8 }}
            >
                <VStack align="start" spacing={1}>
                    <FormLabel m={0} color="whiteAlpha.800" fontSize="lg">
                        {props.label}
                    </FormLabel>
                    {props.description && (
                        <Text color="whiteAlpha.600" maxW="24rem">
                            {props.description}
                        </Text>
                    )}
                </VStack>
                {props.children}
            </FormControl>
        );
    };

    const ChakraCreatableSelect = chakra(CreatableSelect<FormElementOption, true>, {
        baseStyle: {
            w: 'full',
        },
    });

    const SelectComponents: Partial<
        SelectComponentsConfig<FormElementOption, true, GroupBase<FormElementOption>>
    > = {
        MenuList(props) {
            return (
                <VStack
                    w="full"
                    align="start"
                    spacing={0}
                    bg="whiteAlpha.100"
                    borderRadius="md"
                    borderWidth={1}
                    borderColor="whiteAlpha.200"
                    fontSize="sm"
                    fontWeight="medium"
                    py={1}
                    zIndex={10}
                >
                    <HStack py={2} px={3} userSelect="none">
                        <Text fontWeight="semibold" fontSize="xs" color="whiteAlpha.500">
                            Select an option
                        </Text>
                    </HStack>
                    <VStack w="full" align="start" spacing={0}>
                        {props.children}
                    </VStack>
                </VStack>
            );
        },
        Option(props) {
            return (
                <HStack
                    px={1}
                    w="full"
                    onClick={props.selectOption.bind(null, props.data)}
                >
                    <HStack
                        w="full"
                        spacing={2}
                        px={3}
                        py={2}
                        color="whiteAlpha.800"
                        userSelect="none"
                        borderRadius="md"
                        {...(props.isFocused
                            ? {
                                  bg: 'whiteAlpha.100',
                                  _hover: { bg: 'whiteAlpha.100' },
                              }
                            : {
                                  _hover: { bg: 'whiteAlpha.50' },
                              })}
                    >
                        <Text>{props.data.icon.text}</Text>
                        <Text>{props.data.label}</Text>
                    </HStack>
                </HStack>
            );
        },
        MultiValueLabel(props) {
            return (
                <HStack w="full" align="start" spacing={2}>
                    <Text>{props.data.icon.text}</Text>
                    <Text>{props.data.label}</Text>
                </HStack>
            );
        },
    };

    const Element: React.FC<FormElementViewProps> = (props) => {
        if (props.kind === 'input') {
            return (
                <Input
                    {...props.input}
                    borderColor="transparent"
                    bg="whiteAlpha.50"
                    _hover={{
                        borderColor: 'whiteAlpha.200',
                    }}
                    _focus={{
                        outline: 'none',
                        boxShadow: 'none',
                        borderColor: 'whiteAlpha.300',
                    }}
                    _focusVisible={{
                        outline: 'none',
                        boxShadow: 'none',
                        borderColor: 'whiteAlpha.300',
                    }}
                />
            );
        }
        if (props.kind === 'textarea') {
            return (
                <ChakraTextareaAutosize
                    {...props.input}
                    py={2}
                    px={4}
                    maxRows={10}
                    minRows={2}
                    borderRadius="md"
                    resize="none"
                    minH="auto"
                    w="full"
                    borderColor="transparent"
                    bg="whiteAlpha.50"
                    borderWidth={1}
                    transition="border-color 0.2s ease-in-out"
                    _hover={{
                        borderColor: 'whiteAlpha.200',
                    }}
                    _focus={{
                        outline: 'none',
                        boxShadow: 'none',
                        borderColor: 'whiteAlpha.300',
                    }}
                    _focusVisible={{
                        outline: 'none',
                        boxShadow: 'none',
                        borderColor: 'whiteAlpha.300',
                    }}
                    _placeholder={{
                        color: 'whiteAlpha.400',
                    }}
                />
            );
        }
        if (props.kind === 'multi_select') {
            return (
                <ChakraCreatableSelect
                    {...props.input}
                    components={SelectComponents}
                    chakraStyles={{
                        container: (base) => ({
                            ...base,
                            w: 'full',
                        }),
                        control: (base) => ({
                            ...base,
                            _focusVisible: { outline: 'none' },
                        }),
                        dropdownIndicator: (base) => ({
                            ...base,
                            color: 'whiteAlpha.400',
                        }),
                        clearIndicator: (base) => ({
                            ...base,
                            color: 'whiteAlpha.400',
                            fontSize: 'xs',
                            _hover: {
                                color: 'whiteAlpha.500',
                            },
                        }),
                        menu: (base) => ({
                            ...base,
                            bg: 'black',
                        }),
                    }}
                />
            );
        }
        assertNever(props);
    };

    return (props) => {
        return (
            <VStack
                align="start"
                w="full"
                spacing={0}
                py={{ base: 0, md: 2, xl: 4, '2xl': 4 }}
            >
                {/* Sticky Toolbar with Undo/Redo */}
                <Box
                    w="full"
                    position="sticky"
                    zIndex={10}
                    top="0"
                    bg="black"
                    borderBottomWidth={2}
                    borderBottomColor="whiteAlpha.200"
                    mb={{ base: 4, xl: 8, '2xl': 12 }}
                >
                    <Box w="full" bg="whiteAlpha.50" py={{ base: 4, xl: 4, '2xl': 6 }}>
                        <HStack w="full" justify="space-between" align="center">
                            <HStack spacing={1}>
                                {props.actions.map((action) => (
                                    <Tooltip
                                        {...action.tooltip}
                                        key={action.id}
                                        placement="bottom"
                                        openDelay={300}
                                        px={2}
                                        py={1}
                                        bg="gray.50"
                                        color="gray.900"
                                        borderRadius="md"
                                        closeOnClick={false}
                                        shouldWrapChildren={true}
                                    >
                                        <IconButton
                                            {...action.button}
                                            onFocus={(event) => event.preventDefault()}
                                            aria-label={action.id}
                                            size="sm"
                                            bg="none"
                                            _hover={{
                                                bg: action.button.isDisabled
                                                    ? 'none'
                                                    : 'whiteAlpha.50',
                                            }}
                                            _disabled={{
                                                opacity: 0.4,
                                                cursor: 'not-allowed',
                                            }}
                                            icon={
                                                <Icon
                                                    fontSize="lg"
                                                    color={
                                                        action.button.isDisabled
                                                            ? 'whiteAlpha.400'
                                                            : 'whiteAlpha.800'
                                                    }
                                                    as={action.Icon}
                                                />
                                            }
                                        />
                                    </Tooltip>
                                ))}
                            </HStack>
                            <HStack spacing={3}>
                                {props.reset.isVisible && (
                                    <Button
                                        {...props.reset.button}
                                        size="sm"
                                        bg="none"
                                        color="whiteAlpha.500"
                                        _hover={{
                                            bg: 'whiteAlpha.100',
                                        }}
                                    >
                                        Reset
                                    </Button>
                                )}

                                <MenuBase {...props.menu} minW="16rem">
                                    <IconButton
                                        onFocus={(event) => event.preventDefault()}
                                        aria-label="More options"
                                        size="sm"
                                        bg="none"
                                        _hover={{
                                            bg: 'whiteAlpha.100',
                                        }}
                                        _expanded={{
                                            bg: 'whiteAlpha.100',
                                        }}
                                        _active={{
                                            bg: 'none',
                                        }}
                                        _focus={{
                                            outline: 'none',
                                        }}
                                        _focusVisible={{
                                            outline: 'none',
                                        }}
                                        icon={
                                            <Icon
                                                as={HiDotsHorizontal}
                                                color="whiteAlpha.500"
                                                fontSize="lg"
                                            />
                                        }
                                    />
                                </MenuBase>

                                {/* Conditional Save/Share Button */}
                                {props.isDirty ? (
                                    <Button
                                        {...props.save.button}
                                        size="sm"
                                        colorScheme="blue"
                                        bg="blue.500"
                                        color="whiteAlpha.900"
                                        fontWeight="semibold"
                                        iconSpacing={1}
                                        _hover={{
                                            bg: 'blue.600',
                                        }}
                                        _active={{
                                            bg: 'blue.700',
                                        }}
                                    >
                                        {props.save.label}
                                    </Button>
                                ) : (
                                    <ShareContainer
                                        as={ShareMenu}
                                        {...props.share.popover}
                                    >
                                        <Button
                                            size="sm"
                                            colorScheme="blue"
                                            bg="blue.500"
                                            color="whiteAlpha.900"
                                            fontWeight="semibold"
                                            iconSpacing={1}
                                            _hover={{
                                                bg: 'blue.600',
                                            }}
                                            _active={{
                                                bg: 'blue.700',
                                            }}
                                        >
                                            <Text>{props.share.button.label}</Text>
                                        </Button>
                                    </ShareContainer>
                                )}
                            </HStack>
                        </HStack>
                    </Box>
                </Box>
                <VStack
                    maxW={{ base: '42rem' }}
                    w="full"
                    mx="auto"
                    spacing={{ base: 4, md: 8, xl: 12, '2xl': 16 }}
                >
                    <SectionList>
                        {props.form.groups.map((group) => (
                            <SectionItem
                                key={group.id}
                                variant="bordered"
                                title={group.label}
                                description={group.description}
                            >
                                {group.fields.map((field) => (
                                    <InputControl
                                        key={field.id}
                                        label={field.label}
                                        description={field.description}
                                    >
                                        <Element {...field.element} />
                                    </InputControl>
                                ))}
                            </SectionItem>
                        ))}
                        <SectionItem
                            variant="unstyled"
                            title="Qualification"
                            description={
                                <>
                                    Qualify respondents with specific questions to enrich
                                    submissions with structured metadata
                                </>
                            }
                        >
                            <FieldListContainer {...props.field} />
                        </SectionItem>
                    </SectionList>
                </VStack>
            </VStack>
        );
    };
}
